import type { FormContext, ScreenContext } from "@/types";

/**
 * Suggested prompts for the assistant's empty state.
 *
 * The panel used to show one account-wide list ("How are my company's emissions
 * trending this quarter?") on every page, which is useless while the user is
 * halfway through the facility wizard. These are derived from the page the user
 * is actually on, so the chips offer guidance for the work in front of them:
 * while filling the form, "Help me fill out Facility Boundary" rather than a
 * reporting question that has nothing to do with the current screen.
 *
 * Ordering matters: the first chips are the most likely next question on that
 * screen.
 */
const GENERIC_QUESTIONS = [
  "How are my company's emissions trending this quarter?",
  "What are the GRI disclosure requirements for emissions?",
  "Help me generate a sustainability report",
  "What are the latest trends in carbon accounting?",
];

const FACILITY_BASIC_QUESTIONS = [
  "Help me fill out Facility Boundary",
  "What industry category should I select?",
  "Which facility type should I choose?",
  "What fields are required on this step?",
];

const FACILITY_PREFERENCES_QUESTIONS = [
  "Which emission categories should I select?",
  "Which emission factor source should I use?",
  "What does 'Log Emission Against' mean?",
  "Which GWP version should I use?",
];

const FACILITY_LIST_QUESTIONS = [
  "Which facilities still need emissions data?",
  "How do I filter this list by facility type?",
  "How do I export this list?",
  "How do I add a new facility?",
];

const EMISSIONS_QUESTIONS = [
  "How do I upload emissions data?",
  "What does 'possible duplicate' mean?",
  "How is my emissions data validated?",
  "Which emission factors are available?",
];

const DASHBOARD_QUESTIONS = [
  "How are my emissions trending over the selected period?",
  "Which facilities contribute the most emissions?",
  "Which categories contribute the most emissions?",
  "How do I compare emissions across scopes?",
];

const TARGETS_QUESTIONS = [
  "How do I set an emissions target?",
  "Which target type should I use?",
  "How far am I from my target?",
  "How do I report progress against a target?",
];

const REPORTS_QUESTIONS = [
  "Which reports can I generate?",
  "How do I generate a sustainability report?",
  "What is included in the emissions report?",
  "How do I schedule a recurring report?",
];

export interface SuggestedQuestionContext {
  formContext?: FormContext | null;
  screenContext?: ScreenContext | null;
}

/** Map a screen identifier / route hint onto a question set. */
function byScreen(activeView?: string): string[] | null {
  const view = activeView?.toLowerCase() ?? "";
  if (!view) return null;
  if (view.includes("dashboard")) return DASHBOARD_QUESTIONS;
  if (view.includes("facilit") && !view.includes("list")) {
    return FACILITY_BASIC_QUESTIONS;
  }
  if (view.includes("facilit")) return FACILITY_LIST_QUESTIONS;
  if (view.includes("emission")) return EMISSIONS_QUESTIONS;
  if (view.includes("target")) return TARGETS_QUESTIONS;
  if (view.includes("report")) return REPORTS_QUESTIONS;
  return null;
}

/**
 * Picks the suggested prompts for the current page. Falls back to the
 * account-wide questions when the page is unknown, so the empty state is never
 * blank.
 */
export function getSuggestedQuestions({
  formContext,
  screenContext,
}: SuggestedQuestionContext = {}): string[] {
  // The facility wizard is the most specific signal: it knows which step the
  // user is on, which is exactly what the generic screen context cannot say.
  if (formContext?.form === "facility") {
    return formContext.step === "preferences"
      ? FACILITY_PREFERENCES_QUESTIONS
      : FACILITY_BASIC_QUESTIONS;
  }

  return (
    byScreen(screenContext?.activeView) ??
    // Key metrics are a decent proxy when there is no route hint: a page full
    // of facility counts is the facilities list.
    (Object.keys(screenContext?.keyMetrics ?? {}).some((k) =>
      /facilit/i.test(k),
    )
      ? FACILITY_LIST_QUESTIONS
      : null) ??
    GENERIC_QUESTIONS
  );
}
